import { Minus, Plus, Lock } from "lucide-react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart, mixText, boxBags, lineBags } from "@/lib/cart";
import { money, useFlavourStock } from "@/lib/shop";
import { track } from "@/lib/track";
import { FREE_SHIP_MIN, FLAVOURS } from "@/data/content";

const ShipMeter = ({ subtotal }) => {
  const left = FREE_SHIP_MIN - subtotal;
  return (
    <div data-testid="cart-free-shipping-meter" className="mb-4">
      <p className="text-sm font-semibold mb-2">{left > 0 ? `You're ${money(left)} away from free shipping` : "You've unlocked free shipping!"}</p>
      <div className="h-2 rounded-full bg-[#1B1530]/10 overflow-hidden">
        <div className="h-full rounded-full bg-[#7BD88F] transition-[width] duration-500" style={{ width: `${Math.min(100, (subtotal / FREE_SHIP_MIN) * 100)}%` }} />
      </div>
    </div>
  );
};

const Line = ({ item }) => {
  const { setQty, items } = useCart();
  const stock = useFlavourStock();
  const packed = boxBags(items);
  // another copy of this box must still fit in each flavour's stock
  const canMore = lineBags(item).every((m) => {
    const f = FLAVOURS.find((x) => x.name === m.name);
    return !f || (packed[m.name] || 0) + m.qty <= stock[f.key];
  });
  return (
    <div data-testid={`cart-item-${item.key}`} className="flex gap-4 py-4 border-b border-[#1B1530]/10">
      <div className="h-20 w-20 rounded-xl bg-[#BDE3FA] overflow-hidden shrink-0"><img src={item.image} alt="" className="h-full w-full object-contain" /></div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold leading-tight">{item.title}</p>
        <p className="text-sm text-[#4A4560] mt-1">{money(item.price)}</p>
        {item.mix && <p data-testid="cart-item-mix" className="text-xs text-[#4A4560] mt-1 leading-snug">{mixText(item.mix)}</p>}
        {item.issue && (
          <p data-testid="cart-item-issue" className="text-xs text-[#E8233F] font-semibold mt-1 leading-snug">
            {item.issue === "gone" ? "No longer available. Please remove it." : "Not enough bags of this flavour left. Lower the quantity or remove it."}
          </p>
        )}
        <div className="mt-2 inline-flex items-center rounded-full border border-[#1B1530]/15">
          <button data-testid="cart-item-decrease" onClick={() => setQty(item.key, item.qty - 1)} className="h-8 w-8 grid place-items-center hover:text-[#FF5A5F]"><Minus className="h-3.5 w-3.5" /></button>
          <span data-testid="cart-item-qty" className="w-6 text-center text-sm font-bold">{item.qty}</span>
          <button data-testid="cart-item-increase" disabled={!canMore} title={canMore ? undefined : "No more of this flavour in stock"} onClick={() => setQty(item.key, item.qty + 1)} className="h-8 w-8 grid place-items-center hover:text-[#FF5A5F] disabled:opacity-30 disabled:hover:text-inherit"><Plus className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <p className="font-display text-xl">{money(item.price * item.qty)}</p>
    </div>
  );
};

export const CartDrawer = () => {
  const { items, open, setOpen, subtotal, checkoutUrl, count, gift, blocked } = useCart();
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer-container" data-lenis-prevent className="w-full sm:max-w-md bg-[#FFF6EA] text-[#1B1530] flex flex-col p-0">
        <div className="p-6 pb-2">
          <SheetTitle className="font-display text-4xl">Your cart ({count})</SheetTitle>
          <SheetDescription className="text-[#4A4560]">Every bag helps an 18-year-old's dream grow.</SheetDescription>
        </div>
        <div className="flex-1 overflow-y-auto px-6">
          {items.length ? items.map((i) => <Line key={i.key} item={i} />) : (
            <div data-testid="cart-empty" className="py-16 text-center">
              <p className="text-[#4A4560]">Your cart is empty.</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="mt-4 inline-block text-[#FF5A5F] font-bold underline">Browse flavours</Link>
            </div>
          )}
        </div>
        <div className="p-6 border-t border-[#1B1530]/10 bg-white">
          {items.length > 0 && <ShipMeter subtotal={subtotal} />}
          {gift && (gift.code || gift.note) && (
            <p data-testid="cart-gift-applied" className="mb-3 rounded-xl bg-[#FFB21A]/25 px-3 py-2 text-sm font-semibold">
              Mystery gift: {gift.code ? `code ${gift.code} applied at checkout` : "free surprise bag added to your order"}
            </p>
          )}
          <div className="flex justify-between text-lg font-semibold mb-1"><span>Subtotal</span><span data-testid="cart-subtotal">{money(subtotal)}</span></div>
          <p className="text-xs text-[#4A4560] mb-4">Shipping and taxes calculated at checkout.</p>
          {blocked && <p data-testid="cart-blocked-note" className="mb-3 text-sm text-[#E8233F] font-semibold">Some items in your cart are out of stock. Update them to check out.</p>}
          <a data-testid="cart-checkout-button" href={checkoutUrl || undefined} aria-disabled={!checkoutUrl} onClick={() => checkoutUrl && track("checkout")}
            className={`flex items-center justify-center gap-2 h-14 rounded-full font-bold transition-colors ${checkoutUrl ? "bg-[#FF5A5F] text-white hover:bg-[#1B1530]" : "bg-[#1B1530]/10 text-[#4A4560] pointer-events-none"}`}>
            <Lock className="h-4 w-4" /> Secure checkout
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
};
